import { FormField } from '@/components/ui/form-field'
import InfoIcons from '@/icons/InfoIcons'
import { Contract } from '@/modules/Contract/types'
import { Path, Controller, Control } from 'react-hook-form'
import { Button, TextField, Checkbox } from 'vnetwork-ui'
import { TFunction } from 'i18next'

interface CommitmentFieldsConfig {
    renderEditComponent: (control: Control<Contract>) => React.ReactNode
    className: string
}

interface GetCommitmentFieldsParams {
    name: string
    index: number
    isPOC: boolean
    t: TFunction
    onShowHelp: () => void
}

/**
 * Generate commitment form fields configuration based on product_form
 * Returns empty array for 'not_commit' form
 */
export function getCommitmentFields(
    productForm: string,
    params: GetCommitmentFieldsParams
): CommitmentFieldsConfig[] {
    const { name, index, isPOC, t, onShowHelp } = params

    if (productForm !== 'commit') {
        return []
    }

    return [
        // Commitment Value
        {
            renderEditComponent: (control: Control<Contract>) => (
                <div data-tour="bandwidth-commitment-value">
                    <FormField
                        control={control}
                        name={`${name}.[${index}].commitment_value`}
                        type="number"
                        label={t('commitment_value')}
                        placeholder={t('commitment_value')}
                        suffix="GB"
                    />
                </div>
            ),
            className: isPOC ? 'col-span-4' : 'col-span-2',
        },
        // Price per Unit
        {
            renderEditComponent: (control: Control<Contract>) => (
                <div data-tour="bandwidth-price-per-unit">
                    <FormField
                        control={control}
                        name={`${name}.[${index}].commitment_price_per_unit`}
                        type="number"
                        label={t('commitment_price_per_unit')}
                        placeholder={t('commitment_price_per_unit')}
                    />
                </div>
            ),
            className: isPOC ? 'hidden' : '',
        },
        // Price Total (calculated, disabled)
        {
            renderEditComponent: (control: Control<Contract>) => (
                <div data-tour="bandwidth-price-total">
                    <FormField
                        control={control}
                        name={`${name}.[${index}].commitment_price_total`}
                        type="number"
                        label={t('commitment_price_total')}
                        placeholder={t('commitment_price_total')}
                        disabled
                    />
                </div>
            ),
            className: isPOC ? 'hidden' : '',
        },
        // Prepaid Checkbox
        {
            renderEditComponent: (control: Control<Contract>) => (
                <div data-tour="bandwidth-prepaid">
                    <Controller
                        control={control}
                        name={
                            `${name}.[${index}].commitment_prepaid` as Path<Contract>
                        }
                        render={({ field }) => (
                            <Checkbox
                                {...field}
                                checked={field.value as boolean}
                                onChange={(e) =>
                                    field.onChange(e.target.checked)
                                }
                                label={t('commits.commitment_prepaid')}
                            />
                        )}
                    />
                </div>
            ),
            className: isPOC ? 'hidden' : 'col-span-2',
        },
        // Commitment Time
        {
            renderEditComponent: (control: Control<Contract>) => (
                <div data-tour="bandwidth-commitment-time">
                    <Controller
                        control={control}
                        name={
                            `${name}.[${index}].commitment_time` as Path<Contract>
                        }
                        render={({ field }) => (
                            <TextField
                                {...field}
                                type="number"
                                label={t('commitment_time')}
                                placeholder={t('commitment_time')}
                                size="small"
                            />
                        )}
                    />
                </div>
            ),
            className: isPOC ? 'hidden' : '',
        },
        // Help Button
        {
            className: isPOC ? 'hidden' : '',
            renderEditComponent: () => (
                <Button
                    id="bandwidth-help-button"
                    size="small"
                    variant="outlined"
                    className="w-full"
                    startIcon={<InfoIcons className="w-4 h-4" />}
                    onClick={onShowHelp}
                >
                    {t('commitment_time')}
                </Button>
            ),
        },
    ]
}
